import PropTypes from 'prop-types'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import EventEmitter, {
  DROPDOWN_RESET_POSITION,
  SCROLL,
} from '../../helpers/EventEmitter'
import { Element } from '../html/element'
import { calculateDropdownPosition } from './helpers'
import classes from './index.module.scss'

export const positionDropdown = {
  TOP_CENTER: 'topCenter',
  TOP_START_LEFT: 'topStartLeft',
  TOP_START_RIGHT: 'topStartRight',
  TOP_END_LEFT: 'topEndLeft',
  TOP_END_RIGHT: 'topEndRight',
  BOTTOM_CENTER: 'bottomCenter',
  BOTTOM_START_LEFT: 'bottomStartLeft',
  BOTTOM_START_RIGHT: 'bottomStartRight',
  BOTTOM_END_LEFT: 'bottomEndLeft',
  BOTTOM_END_RIGHT: 'bottomEndRight',
  LEFT_CENTER: 'leftCenter',
  LEFT_START: 'leftStart',
  LEFT_END: 'leftEnd',
  RIGHT_CENTER: 'rightCenter',
  RIGHT_START: 'rightStart',
  RIGHT_END: 'rightEnd',
}

const stylePosition = ['top', 'right', 'bottom', 'left']

export const Dropdown = forwardRef(
  (
    {
      children,
      parentRef,
      position,
      appendStyle,
      customPosition,
      dropdownSpace,
      zIndex,
      isTooltip,
      scrollId,
      setIsShow,
      styleDropdown,
    },
    ref,
  ) => {
    const dropdownRef = useRef()
    const [dropdownNode, setDropdownNode] = useState(null)

    useImperativeHandle(
      ref,
      () => ({
        getDropdownRef: () => dropdownRef,
      }),
      [dropdownNode],
    )

    useEffect(() => {
      const newDropdown = document.createElement('div')
      newDropdown.className = classes.container
      newDropdown.style.zIndex = zIndex
      document.body.appendChild(newDropdown)
      setDropdownNode(newDropdown)

      return () => document.body.removeChild(newDropdown)
    }, [])

    useEffect(() => {
      if (dropdownNode) {
        Object.keys(styleDropdown).forEach((key) => {
          dropdownNode.style[key] = styleDropdown[key]
        })
      }
    }, [styleDropdown, dropdownNode])

    useEffect(() => {
      if (dropdownNode)
        EventEmitter.subscribe(DROPDOWN_RESET_POSITION, funcResetPosition)
      return () =>
        EventEmitter.unsubscribe(DROPDOWN_RESET_POSITION, funcResetPosition)
    }, [dropdownNode, parentRef.current?.getBoundingClientRect()])

    useEffect(() => {
      EventEmitter.dispatch(DROPDOWN_RESET_POSITION)
      if (dropdownNode) dropdownNode.style.opacity = 1
    }, [dropdownNode, parentRef.current?.getBoundingClientRect()])

    useEffect(() => {
      if (dropdownNode) {
        if (isTooltip) {
          dropdownNode.classList.remove(classes.boxShadow)
        } else {
          dropdownNode.classList.add(classes.boxShadow)
        }
      }
    }, [isTooltip, dropdownNode])

    useEffect(() => {
      const hiddenPopup = (data) => {
        if (!data || !scrollId) return
        if (
          (Array.isArray(scrollId) && scrollId.includes(data)) ||
          data === scrollId
        ) {
          setIsShow(false)
        }
      }

      EventEmitter.subscribe(SCROLL, hiddenPopup)
      return () => {
        EventEmitter.unsubscribe(SCROLL, hiddenPopup)
      }
    }, [])

    const funcResetPosition = () => {
      if (dropdownNode && parentRef.current) {
        let nodePosition = calculateDropdownPosition(
          parentRef,
          dropdownRef,
          position,
          dropdownSpace,
        )
        if (customPosition) {
          nodePosition = customPosition(nodePosition)
        }
        Object.keys(nodePosition).forEach((key) => {
          if (
            stylePosition.includes(key) &&
            nodePosition[key] &&
            typeof Number(nodePosition[key]) === 'number'
          ) {
            dropdownNode.style[key] = nodePosition[key] + 'px'
          }
        })
      }
    }

    return (
      <>
        {dropdownNode &&
          createPortal(
            <Element
              ref={dropdownRef}
              style={{
                fontSize: 12,
                color: '#191D25',
                ...appendStyle,
              }}
            >
              {children}
            </Element>,
            dropdownNode,
          )}
      </>
    )
  },
)

Dropdown.propTypes = {
  children: PropTypes.node.isRequired,
  parentRef: PropTypes.object.isRequired,
  position: PropTypes.string.isRequired,
  appendStyle: PropTypes.object,
  dropdownSpace: PropTypes.number,
  customPosition: PropTypes.func,
  zIndex: PropTypes.number,
  isTooltip: PropTypes.bool,
  scrollId: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  setIsShow: PropTypes.func,
  styleDropdown: PropTypes.object,
}

Dropdown.defaultProps = {
  appendStyle: {},
  dropdownSpace: 0,
  customPosition: null,
  zIndex: 12,
  isTooltip: false,
  setIsShow: () => {},
  styleDropdown: {},
}
